import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { sanitizeHtml } from '../lib/sanitize';
import InquiryForm from '../components/InquiryForm';
import ImageGallery from '../components/ImageGallery';
import InteractiveMap from '../components/InteractiveMap';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MapPin, Building, Layers, Home, ArrowLeft,
  Square, BedDouble, Euro, CheckCircle, Clock, XCircle,
  ChevronDown, ChevronUp, Image, ExternalLink, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { t } = useTranslation();
  const { market } = useMarket();
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [expandedFloors, setExpandedFloors] = useState({});

  useEffect(() => { fetchPropertyData(); }, [propertyId, market]);

  const fetchPropertyData = async () => {
    try {
      const [propRes, buildingsRes, floorsRes, aptsRes] = await Promise.all([
        axios.get(`${API}/properties/${propertyId}`, { params: { market } }),
        axios.get(`${API}/buildings?property_id=${propertyId}`),
        axios.get(`${API}/floors`),
        axios.get(`${API}/apartments?property_id=${propertyId}`)
      ]);
      setProperty(propRes.data);
      setBuildings(buildingsRes.data);
      setFloors(floorsRes.data);
      setApartments(aptsRes.data);
      if (buildingsRes.data.length > 0) setSelectedBuilding(buildingsRes.data[0]);

      try {
        const filesRes = await axios.get(`${API}/files?entity_id=${propertyId}`);
        const aptIds = (aptsRes.data || []).map(a => a.id);
        const buildingIds = (buildingsRes.data || []).map(b => b.id);
        let allFiles = filesRes.data || [];
        for (const id of [...buildingIds, ...aptIds]) {
          try { const r = await axios.get(`${API}/files?entity_id=${id}`); allFiles = [...allFiles, ...(r.data || [])]; } catch {} // eslint-disable-line no-empty
        }
        setFiles(allFiles);
      } catch {} // eslint-disable-line no-empty
    } catch (error) { console.error('Error fetching property data:', error); } finally { setLoading(false); }
  };

  const getFloorsForBuilding = (buildingId) => floors.filter(f => f.building_id === buildingId).sort((a, b) => a.floor_number - b.floor_number);
  const getApartmentsForFloor = (floorId) => apartments.filter(a => a.floor_id === floorId);
  const getFilesForEntity = (entityId) => files.filter(f => f.entity_id === entityId);

  // ✅ ОПРАВЕНО: премахнат дублиран .map(), правилна проверка за абсолютен URL
  const getImageUrls = (entityId) =>
    getFilesForEntity(entityId)
      .filter(f => f.content_type?.startsWith('image/'))
      .map(f => f.url?.startsWith('http') ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`)
      .filter(url => url && url.length > 10);

  // getDocUrls - proxy URL за правилно сваляне с оригинално име
  const getDocUrls = (entityId) =>
    getFilesForEntity(entityId)
      .filter(f => !f.content_type?.startsWith('image/'))
      .map(f => {
        const isPdf = f.content_type === 'application/pdf' || f.original_filename?.toLowerCase().endsWith('.pdf');
        const proxyUrl = f.id
          ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}?download=true`
          : (f.url?.startsWith('http') ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`);
        return {
          url: proxyUrl,
          original_name: f.original_filename || f.original_name || f.filename || 'Документ',
          content_type: f.content_type || ''
        };
      });

  const toggleFloor = (floorId) => setExpandedFloors(prev => ({ ...prev, [floorId]: !prev[floorId] }));

  // Social links helpers
  const getSocialIcon = (url) => {
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      if (domain.includes('facebook')) return { icon: 'facebook', color: '#1877F2', label: 'Facebook' };
      if (domain.includes('instagram')) return { icon: 'instagram', color: '#E4405F', label: 'Instagram' };
      if (domain.includes('youtube')) return { icon: 'youtube', color: '#FF0000', label: 'YouTube' };
      if (domain.includes('tiktok')) return { icon: 'tiktok', color: '#000000', label: 'TikTok' };
      if (domain.includes('linkedin')) return { icon: 'linkedin', color: '#0A66C2', label: 'LinkedIn' };
      if (domain.includes('twitter') || domain.includes('x.com')) return { icon: 'twitter', color: '#1DA1F2', label: 'X / Twitter' };
      return { icon: 'link', color: '#666666', label: 'Линк' };
    } catch { return { icon: 'link', color: '#666666', label: 'Линк' }; }
  };

  const SocialIcon = ({ type, color }) => {
    if (type === 'facebook') return (
      <svg viewBox="0 0 24 24" fill={color} width="20" height="20"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    );
    if (type === 'instagram') return (
      <svg viewBox="0 0 24 24" fill={color} width="20" height="20"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
    );
    if (type === 'youtube') return (
      <svg viewBox="0 0 24 24" fill={color} width="20" height="20"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
    );
    if (type === 'tiktok') return (
      <svg viewBox="0 0 24 24" fill={color} width="20" height="20"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
    );
    if (type === 'linkedin') return (
      <svg viewBox="0 0 24 24" fill={color} width="20" height="20"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    );
    // Default link icon
    return <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" width="20" height="20"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'reserved': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'sold': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return t('property.available');
      case 'reserved': return t('property.reserved');
      case 'sold': return t('property.sold');
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'available': return 'status-available';
      case 'reserved': return 'status-reserved';
      case 'sold': return 'status-sold';
      default: return '';
    }
  };

  const formatPrice = (price) => new Intl.NumberFormat('bg-BG', { style: 'currency', currency: 'EUR' }).format(price);

  if (loading) return (
    <div className="min-h-screen bg-background"><Header /><div className="flex items-center justify-center min-h-[60vh]"><div className="spinner"></div></div><Footer /></div>
  );

  if (!property) return (
    <div className="min-h-screen bg-background"><Header />
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-lg text-muted-foreground">{t('property.not_found')}</p>
        <Link to="/properties"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />{t('property.back')}</Button></Link>
      </div>
    <Footer /></div>
  );

  // Use files from API as primary source, fallback to property.images only if no files
  const filesImages = getImageUrls(propertyId);
  const propertyImages = (() => {
    let imgs;
    if (filesImages.length > 0) {
      imgs = filesImages;
    } else {
      // Combine main_image + images[], deduplicate by URL
      const all = [...(property.images || [])];
      if (property.main_image && !all.includes(property.main_image)) {
        all.unshift(property.main_image);
      }
      imgs = all;
    }
    // Final dedup - remove broken/empty URLs
    return [...new Set(imgs)].filter(url => url && url.length > 10);
  })();
  // Docs from files API with full metadata
  const propertyDocs = getDocUrls(propertyId).length > 0
    ? getDocUrls(propertyId)
    : (property.documents || []).map(d => typeof d === 'string' ? { url: d, original_name: 'Документ', content_type: '' } : d);
  const selectedAptImages = selectedApartment ? [...(selectedApartment.images || []), ...(selectedApartment.blueprint_image ? [selectedApartment.blueprint_image] : []), ...getImageUrls(selectedApartment.id)] : [];

  // Localized description with fallback to Bulgarian if this market's translation is missing
  const DESC_FIELD_BY_LANG = { bg: 'description', el: 'description_el', tr: 'description_tr', en: 'description_en' };
  const localizedDescription = (() => {
    const field = DESC_FIELD_BY_LANG[market] || 'description';
    const text = property[field];
    return (text && text.trim()) ? text : property.description;
  })();

  return (
    <div className="min-h-screen bg-background" data-testid="property-detail-page">
      <SEO
        title={property.name}
        description={localizedDescription?.slice(0, 160)}
        image={property.images?.[1] || property.images?.[0] || property.main_image}
        url={`/properties/${propertyId}`}
        type="article"
      />
      <Header />
      <section className="pt-20">
        <div className="relative h-[50vh] min-h-[400px]">
          <img src={property.images?.[1] || property.images?.[0] || property.main_image || '/og-image.jpg'} alt={property.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-7xl mx-auto">
              <Link to="/properties" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('property.back')}
              </Link>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">{property.name}</h1>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-white/80"><MapPin className="w-5 h-5" /><span>{property.location}</span></div>
                <Badge className={getStatusClass(property.status)}>
                  {property.status === 'active' ? t('property.in_construction') : property.status === 'completed' ? t('property.completed') : property.status === 'upcoming' ? t('properties.status_upcoming') : property.status}
                </Badge>
              </div>
              {property.social_links && property.social_links.filter(l => l).length > 0 && (
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  {property.social_links.filter(l => l).map((link, idx) => {
                    const { icon, color, label } = getSocialIcon(link);
                    return (
                      <a key={idx} href={link} target="_blank" rel="noopener noreferrer"
                        title={label}
                        className="flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm px-3 py-1.5 rounded-full transition-colors">
                        <SocialIcon type={icon} color="white" />
                        <span className="text-white text-sm font-medium">{label}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="mb-8">
                  <TabsTrigger value="overview" data-testid="tab-overview">{t('property.overview')}</TabsTrigger>

                  <TabsTrigger value="gallery" data-testid="tab-gallery">{t('property.gallery')}</TabsTrigger>
                  {(property.latitude && property.longitude) && (
                    <TabsTrigger value="location" data-testid="tab-location">{t('property.location')}</TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="overview">
                  <div className="prose max-w-none">
                    <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">{t('property.about_project')}</h2>
                    <div 
                      className="text-muted-foreground leading-relaxed prose prose-sm max-w-none prose-a:text-[#1B5E37] prose-a:underline"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(localizedDescription || '').replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ') }}
                    />
                  </div>

                  {/* Social Links section */}
                  {property.social_links && property.social_links.filter(l => l).length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-lg font-semibold text-foreground mb-3">{t('property.follow')}</h3>
                      <div className="flex items-center gap-3 flex-wrap">
                        {property.social_links.filter(l => l).map((link, idx) => {
                          const { icon, color, label } = getSocialIcon(link);
                          return (
                            <a key={idx} href={link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all hover:scale-105 hover:shadow-md font-medium text-sm text-white"
                              style={{ backgroundColor: color, borderColor: color }}>
                              <SocialIcon type={icon} color="white" />
                              {label}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Documents section */}
                  {propertyDocs.length > 0 && (
                    <div className="mt-10">
                      <h3 className="font-heading text-xl font-semibold text-foreground mb-4">{t('property.documents')}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {propertyDocs.map((doc, idx) => {
                          const docUrl = typeof doc === 'string' ? doc : doc.url;
                          const docName = typeof doc === 'string'
                            ? `Документ ${idx + 1}`
                            : (doc.original_name || doc.original_filename || doc.filename || doc.name || `Документ ${idx + 1}`);
                          const isPdf = (docUrl?.toLowerCase().includes('.pdf') || docName?.toLowerCase().endsWith('.pdf'));
                          const isDoc = docName?.toLowerCase().match(/\.(doc|docx)$/);
                          const handleDownload = async (e) => {
                            e.preventDefault();
                            try {
                              const res = await fetch(docUrl);
                              const blob = await res.blob();
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = docName;
                              a.click();
                              URL.revokeObjectURL(a.href);
                            } catch {
                              window.open(docUrl, '_blank');
                            }
                          };
                          return (
                            <button key={idx} onClick={handleDownload}
                              className="flex items-center gap-3 p-4 rounded-xl border border-border bg-secondary/30 hover:bg-secondary/60 transition-colors group w-full text-left">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-none ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}>
                                <FileText className={`w-5 h-5 ${isPdf ? 'text-red-600' : 'text-blue-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{docName}</p>
                                <p className="text-xs text-muted-foreground">{isPdf ? 'PDF документ' : isDoc ? 'Word документ' : 'Документ'} • Кликни за сваляне</p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-muted-foreground flex-none opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}


                </TabsContent>

                <TabsContent value="apartments">
                  {buildings.length > 1 && (
                    <div className="mb-8">
                      <h3 className="font-heading text-lg font-semibold mb-4">{t('property.select_building')}</h3>
                      <div className="flex gap-3">
                        {buildings.map((building) => (
                          <Button key={building.id} variant={selectedBuilding?.id === building.id ? "default" : "outline"} onClick={() => setSelectedBuilding(building)} data-testid={`building-btn-${building.id}`}>
                            <Building className="w-4 h-4 mr-2" />{building.name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedBuilding && (
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold">{t('property.floors_and_apts')} - {selectedBuilding.name}</h3>
                      {getFloorsForBuilding(selectedBuilding.id).map((floor) => {
                        const floorImages = getImageUrls(floor.id);
                        return (
                          <div key={floor.id} className="border border-border rounded-xl overflow-hidden">
                            <button onClick={() => toggleFloor(floor.id)} className="w-full flex items-center justify-between p-4 bg-secondary hover:bg-secondary/80 transition-colors" data-testid={`floor-toggle-${floor.id}`}>
                              <div className="flex items-center gap-3">
                                <Layers className="w-5 h-5 text-primary" />
                                <span className="font-medium">{t('property.floor')} {floor.floor_number}</span>
                                <span className="text-sm text-muted-foreground">({getApartmentsForFloor(floor.id).length} {t('property.apartments_count')})</span>
                                {(floor.blueprint_image || floorImages.length > 0) && (
                                  <span className="text-xs bg-[#1B5E37]/10 text-[#1B5E37] px-2 py-0.5 rounded-full flex items-center gap-1"><Image className="w-3 h-3" /> {t('property.plan')}</span>
                                )}
                              </div>
                              {expandedFloors[floor.id] ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                            </button>
                            {expandedFloors[floor.id] && (
                              <div className="p-4">
                                {(floor.blueprint_image || floorImages.length > 0) && (
                                  <div className="mb-4 pb-4 border-b border-border">
                                    <ImageGallery images={[...(floor.blueprint_image ? [floor.blueprint_image] : []), ...floorImages]} title={`${t('property.floor_plan')} ${floor.floor_number}`} />
                                  </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {getApartmentsForFloor(floor.id).map((apt) => (
                                    <div key={apt.id} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedApartment?.id === apt.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'} ${apt.status === 'sold' ? 'opacity-60 cursor-not-allowed' : ''}`} onClick={() => apt.status !== 'sold' && setSelectedApartment(apt)} data-testid={`apartment-card-${apt.id}`}>
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2"><Home className="w-4 h-4 text-primary" /><span className="font-medium">{t('property.apt')} {apt.number}</span></div>
                                        <div className="flex items-center gap-1">{getStatusIcon(apt.status)}<Badge className={getStatusClass(apt.status)}>{getStatusLabel(apt.status)}</Badge></div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-1"><Square className="w-3 h-3" />{apt.area} {t('property.area')}</div>
                                        <div className="flex items-center gap-1"><BedDouble className="w-3 h-3" />{apt.rooms} {t('property.rooms')}</div>
                                        <div className="flex items-center gap-1 text-primary font-medium"><Euro className="w-3 h-3" />{formatPrice(apt.price)}</div>
                                      </div>
                                      {apt.features?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-3">{apt.features.slice(0, 3).map((feature, i) => (<span key={i} className="text-xs bg-secondary px-2 py-1 rounded">{feature}</span>))}</div>
                                      )}
                                      {(apt.images?.length > 0 || apt.blueprint_image || getImageUrls(apt.id).length > 0) && (
                                        <div className="flex items-center gap-1 mt-2 text-xs text-[#1B5E37]">
                                          <Image className="w-3 h-3" />
                                          <span>{(apt.images?.length || 0) + (apt.blueprint_image ? 1 : 0) + getImageUrls(apt.id).length} {t('property.photos')}</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {apartments.length === 0 && (
                    <p className="text-center text-muted-foreground py-12">{t('property.no_apartments')}</p>
                  )}
                </TabsContent>

                <TabsContent value="gallery">
                  <div className="space-y-8">
                    <ImageGallery images={propertyImages} title={`${t('property.photos_of')} ${property.name}`} />
                    {propertyImages.length === 0 && propertyDocs.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>{t('property.no_photos')}</p>
                        <p className="text-sm mt-1">{t('property.photos_from_admin')}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {(property.latitude && property.longitude) && (
                  <TabsContent value="location">
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-foreground mb-4">{t('property.project_location')}</h3>
                      <p className="text-muted-foreground mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" />{property.location}</p>
                      <div className="w-full h-[400px] rounded-xl overflow-hidden border border-border" data-testid="property-map">
                        <InteractiveMap properties={[property]} center={{ lat: property.latitude, lng: property.longitude }} zoom={15} singleMarker={true} />
                      </div>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {selectedApartment && (
                  <div className="p-6 bg-primary/5 border border-primary/20 rounded-xl" data-testid="selected-apartment-details">
                    <h3 className="font-heading font-semibold text-lg mb-4">{t('property.apartments')} {selectedApartment.number}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('property.number')}</span><span className="font-medium">{selectedApartment.number}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('property.area_label')}</span><span className="font-medium">{selectedApartment.area} {t('property.area')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('property.rooms_label')}</span><span className="font-medium">{selectedApartment.rooms}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('property.status')}</span><Badge className={getStatusClass(selectedApartment.status)}>{getStatusLabel(selectedApartment.status)}</Badge></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">{t('property.price')}</span><span className="font-semibold text-primary">{formatPrice(selectedApartment.price)}</span></div>
                      {selectedApartment.description && (<div className="pt-2 border-t border-border"><p className="text-sm text-muted-foreground">{selectedApartment.description}</p></div>)}
                      {selectedApartment.features?.length > 0 && (
                        <div className="pt-2 border-t border-border">
                          <span className="text-sm text-muted-foreground mb-2 block">{t('property.features')}:</span>
                          <div className="flex flex-wrap gap-1">{selectedApartment.features.map((f, i) => (<span key={i} className="text-xs bg-secondary px-2 py-1 rounded">{f}</span>))}</div>
                        </div>
                      )}
                    </div>
                    {selectedAptImages.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <ImageGallery images={selectedAptImages} title={`${t('property.photos_apt')} ${selectedApartment.number}`} />
                      </div>
                    )}
                    <Button className="w-full mt-6" onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })} data-testid="inquiry-for-apartment-btn">
                      {t('property.inquiry_btn')}
                    </Button>
                  </div>
                )}

                <div id="inquiry-form" className="p-6 bg-white border border-border rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg mb-4">{t('property.send_inquiry')}</h3>
                  <InquiryForm propertyId={propertyId} apartmentId={selectedApartment?.id} propertyName={selectedApartment ? `${property.name} - ${t('property.apt')} ${selectedApartment.number}` : property.name} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
